import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useDataService from '../../hooks/useDataService';
import apiToUrlMap, { formatString } from '../../ApiMapping';
import { mapSimulateResponse } from '../../components/PxwProcessDevices/utils';

import { eMessageType } from '../../types/IMessageType';
import { IGeneratedPayload, IGeneratedResult, ISimulateResponse } from './types';

export const usePxwProcessDevices = () => {
  const userRoles = useSelector((state: any) => state.userState.role);

  const [isLoading, setIsLoading] = useState(false);
  const [tenantList, setTenantList] = useState<string[] | null>();
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [generatedResult, setGeneratedResult] = useState<IGeneratedResult | null>(null);

  const { fetchUrl, openSnackBar } = useDataService();

  useEffect(() => {
    const config = JSON.parse(localStorage.getItem('PxConfig') || '{}');
    document.title = String(config.siteTitle) + ' - Pxw Process Devices';
  }, []);

  useEffect(() => {
    if (!userRoles['PXW_SANDBOX']) return;
    const loadTenants = async () => {
      try {
        const result: { tenantIds: string[] } = await fetchUrl(
          'GET',
          apiToUrlMap.pxwDeviceTestSandboxTenants
        );
        setTenantList(result.tenantIds ?? []);
      } catch (e: any) {
        openSnackBar(e.message || 'Unable to fetch tenants', eMessageType.error);
      }
    };
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  const handleTenantChange = (_: any, value: string | null) => {
    setSelectedTenant(value);
    setGeneratedResult(null);
  };

  const handleReset = () => {
    setSelectedTenant(null);
    setGeneratedResult(null);
  };

  const handleSynthesize = async (payload: IGeneratedPayload) => {
    try {
      setIsLoading(true);
      const { selectedItem, ...body } = payload;
      const res: ISimulateResponse = await fetchUrl(
        'POST',
        formatString(apiToUrlMap.pxwDeviceTestSandboxSimulate, { tenantId: selectedTenant }),
        { body }
      );
      setGeneratedResult({
        groupedDevices: mapSimulateResponse(res),
        item: selectedItem,
      });
    } catch (e: any) {
      openSnackBar(e.message || 'Something went wrong', eMessageType.error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    userRoles,
    isLoading,
    tenantList,
    selectedTenant,
    generatedResult,
    handleTenantChange,
    handleReset,
    handleSynthesize,
    onResultInvalidated: () => setGeneratedResult(null),
  };
};
